import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import NotesWorkspace from 'components/NotesWorkspace'
import type { NoteImageRecord, NotesSurfaceAdapter } from 'components/NotesWorkspace'
import {
	createPortfolioNote,
	getPortfolioNotes,
	updatePortfolioNote,
} from 'slices/portfolioNoteSlice'
import {
	createPortfolioImage,
	getPortfolioImages,
	updatePortfolioImage,
	deletePortfolioImage,
} from 'slices/portfolioImagesSlice'
import { patchPortfolio } from 'slices/portfolioSlice'
import { prepareNoteHtml } from 'utils/noteUpload'
import { removeImageFromNoteHtml } from 'utils/noteImages'
import PortfolioLocationNotes from '../components/PortfolioLocationNotes'

/**
 * The portfolio note and image slices predate the typed-slice convention: their
 * payload creators declare no parameter type, so TypeScript infers zero-argument
 * action creators and rejects the payloads they in fact require.
 *
 * Widened here rather than in the slices, which other screens share.
 */
const getNotesThunk = getPortfolioNotes as any
const createNoteThunk = createPortfolioNote as any
const updateNoteThunk = updatePortfolioNote as any
const getImagesThunk = getPortfolioImages as any
const createImageThunk = createPortfolioImage as any
const updateImageThunk = updatePortfolioImage as any
const deleteImageThunk = deletePortfolioImage as any

/**
 * Notes for one portfolio, in the two-column workspace layout.
 *
 * Portfolio notes cannot thread: portfolioNoteModel has no `parentid`, so a
 * reply would be stored as another top-level note. The workspace is told so and
 * hides the reply affordance rather than offering a control that would
 * silently do the wrong thing.
 */
const PortfolioNotes = () => {
	const { id } = useParams<{ id: string }>()
	const dispatch = useDispatch()
	const portfolioId = id as string

	const userInfo = useSelector((state: any) => state.userLogin.userInfo)
	const userId = userInfo?._id

	const portfolio = useSelector((state: any) => state.portfolios.entities[portfolioId])
	const isOwner = portfolio?.owner?._id === userId
	const {
		entities: noteEntities,
		ids: noteIds,
		isLoading,
		error,
	} = useSelector((state: any) => state.portfolioNotes)
	const portfolioImageEntities = useSelector((state: any) => state.portfolioImages.entities)

	useEffect(() => {
		// Fetched unconditionally rather than behind the slice's `isInitialized`
		// gate: this slice is shared, so honouring the gate would render whatever
		// the last screen to touch it happened to leave behind.
		dispatch(getNotesThunk({ portfolioId }) as any)
		dispatch(getImagesThunk(portfolioId) as any)
	}, [dispatch, portfolioId])

	const notes = useMemo(
		() => noteIds.map((noteId: string) => noteEntities[noteId]).filter(Boolean),
		[noteIds, noteEntities],
	)

	const imageRecords = useMemo<NoteImageRecord[]>(
		() =>
			Object.values(portfolioImageEntities || {}).filter(
				(image: any) => image && image.portfolioid === portfolioId,
			) as NoteImageRecord[],
		[portfolioImageEntities, portfolioId],
	)

	const adapter = useMemo<NotesSurfaceAdapter>(
		() => ({
			key: 'portfolio',
			title: 'Notes',
			subtitle: portfolio?.assetName || 'this portfolio',
			supportsReplies: false,

			/*
			 * No `deleteNote` here, and that is deliberate rather than an omission.
			 * `portfolioNoteController` has no delete handler and
			 * `portfolioNoteRoutes` exposes no delete route, so there is nothing to
			 * call. The adapter contract makes `deleteNote` optional precisely so a
			 * surface can decline, and the control hides itself rather than
			 * appearing and failing. Tracked in MYD-570, which also has to decide
			 * what "has comments" means for this model: portfolio notes carry an
			 * embedded `comments` array instead of the `parentid` replies the other
			 * two surfaces count.
			 */
			notes,
			imageRecords,
			isLoading,
			error,
			currentUserId: userId,

			createNote: async ({ contents, libraryImages }) => {
				// The composer hands back one body per note: several when the
				// author chose a separate note per image, one otherwise.
				let wroteImages = 0

				for (const [position, body] of contents.entries()) {
					const { html, uploaded } = await prepareNoteHtml(body, portfolioId, {
						// Library rows belong to the submission, not to each body,
						// so only the first pass reports them. Otherwise N notes
						// would write N copies of the same gallery row.
						libraryImages: position === 0 ? libraryImages : [],
					})

					await dispatch(
						createNoteThunk({
							userid: userId,
							portfolioid: portfolioId,
							note: html,
							parentid: null,
						}) as any,
					)

					for (const image of uploaded) {
						await dispatch(
							createImageThunk({
								userid: userId,
								portfolioid: portfolioId,
								uuid: image.public_id,
								name: image.original_filename,
								size: image.bytes,
								url: image.secure_url,
								type: image.format,
							}) as any,
						)
						wroteImages += 1
					}
				}

				dispatch(
					patchPortfolio({
						portfolioId,
						updates: { updateNote: `New note created by ${userInfo?.name}.` },
					}) as any,
				)

				// Refetched once at the end rather than per note.
				await dispatch(getNotesThunk({ portfolioId }) as any)
				if (wroteImages > 0) await dispatch(getImagesThunk(portfolioId) as any)
			},

			updateImage: async image => {
				await dispatch(updateImageThunk(image) as any)
				await dispatch(getImagesThunk(portfolioId) as any)
			},

			/**
			 * Removing an image takes it out of both places it lives: the note's
			 * HTML and the gallery. Dropping only the row would leave the note
			 * rendering a picture that no longer exists.
			 */
			removeImage: async ({ image, url, noteId, noteBody }) => {
				const nextHtml = removeImageFromNoteHtml(noteBody, url)
				if (noteId && nextHtml !== noteBody) {
					await dispatch(updateNoteThunk({ _id: noteId, note: nextHtml }) as any)
				}
				await dispatch(deleteImageThunk(image._id) as any)
				await dispatch(getNotesThunk({ portfolioId }) as any)
				await dispatch(getImagesThunk(portfolioId) as any)
			},
		}),
		[
			dispatch,
			portfolio?.assetName,
			portfolioId,
			notes,
			imageRecords,
			isLoading,
			error,
			userId,
			userInfo?.name,
		],
	)

	return (
		<div className="space-y-8">
			<NotesWorkspace adapter={adapter} />

			{/* Notes written against the portfolio's locations. A different
			    question from the feed above, so it keeps its own section rather
			    than being merged in. Matches the feed column's width.

			    Owner only, which is the boundary the retired owner/partner split
			    of this screen used to draw: a partner was shown the portfolio's
			    own notes and never the roll-up across its locations. */}
			{isOwner && (
				<PortfolioLocationNotes portfolio={portfolio} className="mx-auto w-full max-w-[640px]" />
			)}
		</div>
	)
}

export default PortfolioNotes
